import { storeToRefs } from 'pinia'
import { Screen } from 'quasar'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import configTheme from '@/config/theme.json'
import { useAppStore } from '@/store/modules/app'
import { ordinal } from '@/utils/number'

import { useTransformField } from './transformField'

const { colors, formats, icons, logo, customerPanel } = configTheme

const barStyle = {
  backgroundColor: colors.scrollBar.barStyleBackground,
  borderRadius: '9px',
  height: '9px',
  opacity: '0.2',
  right: '0px',
  width: '9px',
}

const thumbStyle = {
  backgroundColor: colors.scrollBar.thumbStyleBackground,
  borderRadius: '5px',
  opacity: '0.75',
  right: '4px',
  width: '5px',
}

const favoritesResultsGridCols = computed(
  () => customerPanel.favorites.resultsCols[Screen.name],
)

const getComponentConfig = () => configTheme.components

const breakpoint = computed(() => {
  switch (Screen.name) {
    case 'sm':
      return 'tablet'

    case 'xs':
      return 'mobile'

    case 'md':
    case 'lg':
    case 'xl':
      return 'desktop'

    default:
      return 'desktop'
  }
})

export const useTheme = () => {
  const { locale, t, n } = useI18n()

  const { getLocalizedLabel } = useTransformField()

  const { configTheme } = storeToRefs(useAppStore())

  const formatAuctionRound = (round: number) =>
    `${ordinal(round, locale.value)} ${t('round')}`

  const configPropertyCodeListItem = computed(
    () => configTheme.value?.list_item.component_props.property_code,
  )

  const configPropertyCodeListing = computed(
    () => configTheme.value?.page.listing.component_props.property_code,
  )

  const formatPrice = (
    price: number | undefined,
    formatCurrency: Partial<typeof formats.currency> = {},
  ) => {
    const _price = price ?? 0

    const numberOptions = { ...formats.currency, ...formatCurrency }

    return n(_price, {
      ...numberOptions,
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
      style: 'currency',
    })
      .replaceAll(',', formats.currency.delimiter)
      .replaceAll('.', formats.currency.delimiter)
  }

  const formatPropertyCodeListItem = (code: string) => {
    if (!configPropertyCodeListItem.value) {
      return ''
    }

    const { prefix, visible } = configPropertyCodeListItem.value

    const prefixLocalized = getLocalizedLabel(prefix)

    return (visible && `${prefixLocalized}${code}`) || ''
  }

  const formatPropertyCodeListing = (code?: string | null): string => {
    if (!configPropertyCodeListing.value || !code) {
      return ''
    }

    const { prefix, visible } = configPropertyCodeListing.value

    const prefixLocalized = getLocalizedLabel(prefix)

    return (visible && `${prefixLocalized}${code}`) || ''
  }

  return {
    barStyle,
    breakpoint,
    colors,
    favoritesResultsGridCols,
    formatAuctionRound,
    formatPrice,
    formatPropertyCodeListItem,
    formatPropertyCodeListing,
    getComponentConfig,
    icons,
    logo,
    thumbStyle,
  }
}
